import {Component, inject, Inject, Optional} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ProductService} from '../../../../services/product.service';
import {NotificationService} from '../../../../notification/notification.service';
import {NotificationType} from '../../../../enum/notification-type.enum';
import {THEME_CONFIG} from '../../../../../environments/tokens/theme.token';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatButton} from '@angular/material/button';
import {DecimalPipe, NgOptimizedImage} from '@angular/common';

@Component({
  selector: 'app-details-product-menus',
  templateUrl: './details-product-menus.component.html',
  styleUrl: './details-product-menus.component.scss',
  imports: [MatCheckbox, NgOptimizedImage, MatButton, DecimalPipe]
})
export class DetailsProductMenusComponent {
  product: any;
  loading = true;
  protected readonly themeConfig = inject(THEME_CONFIG)

  selectedSize: string | null = null;
  selectedPrice: number = 0;
  productQuantity = 1;

  selectedIngredients: string[] = [];
  excludedIngredients: string[] = [];

  selectedItemQuantities: { [additionId: string]: { [itemId: string]: number } } = {};
  selectedSupplements: { [supplementId: string]: number } = {};
  supplementsByCategory: { [category: string]: any[] } = {};

  constructor(
    public dialogRef: MatDialogRef<DetailsProductMenusComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      productId: string, product?: any, existingCustomization?: any,
      preselectedSize?: string | null
    },
    private productService: ProductService,
    private notificationService: NotificationService
  ) {
  }

  ngOnInit(): void {
    if (this.data.product) {
      // Produit déjà chargé, pas besoin de fetch
      this.product = this.data.product;
      this.initializeDefaults();
      this.groupSupplementsByCategory();
      this.loading = false;
    } else {
      this.loadProduct(this.data.productId);
    }
  }

  loadProduct(productId: string) {
    this.productService.getProductById(productId).subscribe({
      next: (response) => {
        this.product = response.body?.data;
        this.initializeDefaults();
        this.groupSupplementsByCategory();
        this.loading = false;
      },
      error: (err) => {
        this.notificationService.errorHandler(err);
        this.dialogRef.close();
      }
    });
  }

  initializeDefaults() {
    const existing = this.data.existingCustomization;

    // MODIFIÉ : Utiliser preselectedSize si disponible
    if (this.data.preselectedSize) {
      // Si une taille est présélectionnée depuis la promo
      this.selectedSize = this.data.preselectedSize;

      // Trouver le prix correspondant à cette taille
      const priceForSize = this.product.pricePerSizes?.find(
        (ps: any) => ps.size.name === this.data.preselectedSize
      );
      this.selectedPrice = priceForSize?.price || this.product.price;
    } else if (existing?.selectedSize) {
      // Sinon utiliser la customization existante
      this.selectedSize = existing.selectedSize;
      this.selectedPrice = existing.selectedPrice;
    } else if (this.product.pricePerSizes?.length > 0) {
      // Sinon utiliser la première taille disponible
      this.selectedSize = this.product.pricePerSizes[0].size.name;
      this.selectedPrice = this.product.pricePerSizes[0].price;
    } else {
      // Sinon utiliser le prix fixe
      this.selectedPrice = this.product.price;
    }
    if (existing?.excludedIngredients) {
      this.excludedIngredients = [...existing.excludedIngredients];
      this.selectedIngredients = this.product.ingredients
        .map((i: any) => i.id)
        .filter((id: string) => !this.excludedIngredients.includes(id));
    } else {
      this.selectedIngredients = this.product.ingredients?.map((i: any) => i.id) || [];
      this.excludedIngredients = [];
    }

    this.getFilteredAdditions()?.forEach((addition: any) => {
      this.selectedItemQuantities[addition.id] = {};

      addition?.items?.forEach((item: any) => {
        if (existing?.selectedAdditions?.[addition.id]?.[item.id]) {
          this.selectedItemQuantities[addition.id][item.id] = existing.selectedAdditions[addition.id][item.id];
        } else {
          this.selectedItemQuantities[addition.id][item.id] = 0;
        }
      });
    });
    if (existing?.selectedSupplements) {
      this.selectedSupplements = {...existing.selectedSupplements};
    } else {
      this.selectedSupplements = {};
    }
  }

  getFilteredAdditions(): any[] {
    if (!this.product?.additions) return [];

    if (!this.selectedSize) {
      return [...this.product.additions].sort((a: any, b: any) => a.order - b.order);
    }

    const filtered = this.product.additions.filter((addition: any) => {
      if (!addition.sizes || addition.sizes.length === 0) return true;

      return addition.sizes.some((size: any) => size.name === this.selectedSize);
    });

    return filtered.sort((a: any, b: any) => a.order - b.order);
  }

  truncate2(value: number): number {
    return Math.floor(value * 100) / 100;
  }

  groupSupplementsByCategory() {
    if (!this.product?.supplements) return;
    this.supplementsByCategory = {};
    this.product?.supplements?.forEach((supplement: any) => {
      if (supplement.categories && supplement.categories.length > 0) {
        supplement.categories.forEach((category: string) => {
          if (!this.supplementsByCategory[category]) {
            this.supplementsByCategory[category] = [];
          }
          this.supplementsByCategory[category].push(supplement);
        });
      }
    });
  }

  getSupplementPrice(supplement: any): number {
    if (!this.selectedSize) return supplement.price || 0;
    const priceForSize = supplement.pricePerSizes?.find(
      (ps: any) => ps.size.name === this.selectedSize
    );
    return priceForSize?.price || supplement.price || 0;
  }

  toggleSupplement(supplementId: string) {
    if (this.selectedSupplements[supplementId]) {
      delete this.selectedSupplements[supplementId];
    } else {
      this.selectedSupplements[supplementId] = 1;
    }
  }

  isSupplementSelected(supplementId: string): boolean {
    return !!this.selectedSupplements[supplementId];
  }

  getSupplementCategories(): string[] {
    return Object.keys(this.supplementsByCategory);
  }

  getTotalQuantityForAddition(additionId: string): number {
    if (!this.selectedItemQuantities[additionId]) return 0;
    return Object.values(this.selectedItemQuantities[additionId])
      .reduce((sum: number, qty: number) => sum + qty, 0);
  }

  getAdditionTitle(addition: any): string {
    if (addition.category?.name) return addition.category.name;
    if (addition.categories?.length > 0) return addition.categories[0];
    return 'Choix';
  }

  scrollToNextAddition(index: number) {
    const next = document.getElementById(`addition-${index + 1}`);
    if (next) {
      next.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  onIngredientChange(event: any, ingredientId: string) {
    if (event.checked) {
      if (!this.selectedIngredients.includes(ingredientId)) {
        this.selectedIngredients.push(ingredientId);
      }
      this.excludedIngredients = this.excludedIngredients.filter(id => id !== ingredientId);
    } else {
      this.selectedIngredients = this.selectedIngredients.filter(id => id !== ingredientId);
      if (!this.excludedIngredients.includes(ingredientId)) {
        this.excludedIngredients.push(ingredientId);
      }
    }

  }

  incrementItem(additionId: string, itemId: string, maxQuantity: number, index: number) {
    const totalQty = this.getTotalQuantityForAddition(additionId);

    if (totalQty >= maxQuantity) {
      // ========================================
      // MODIFICATION: Utiliser getFilteredAdditions
      // ========================================
      const addition = this.getFilteredAdditions().find((a: any) => a.id === additionId);
      const categoryName = this.getAdditionTitle(addition);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Vous avez déjà choisi votre "${categoryName}"`,
        ''
      );
      return;
    }

    if (!this.selectedItemQuantities[additionId]) {
      this.selectedItemQuantities[additionId] = {};
    }

    if (!this.selectedItemQuantities[additionId][itemId]) {
      this.selectedItemQuantities[additionId][itemId] = 0;
    }

    this.selectedItemQuantities[additionId][itemId]++;

    const newTotal = this.getTotalQuantityForAddition(additionId);

    if (newTotal === maxQuantity) {
      this.scrollToNextAddition(index);
    }
  }

  decrementItem(additionId: string, itemId: string) {
    if (this.selectedItemQuantities[additionId]?.[itemId] > 0) {
      this.selectedItemQuantities[additionId][itemId]--;
      if (this.selectedItemQuantities[additionId][itemId] === 0) {
        delete this.selectedItemQuantities[additionId][itemId];
      }
    }
  }

  getItemQuantity(additionId: string, itemId: string): number {
    return this.selectedItemQuantities[additionId]?.[itemId] || 0;
  }


  calculateTotal(): number {
    let total = this.selectedPrice || this.product.price;

    // ========================================
    // MODIFICATION: Utiliser getFilteredAdditions
    // ========================================
    this.getFilteredAdditions()?.forEach((addition: any) => {
      addition?.items?.forEach((item: any) => {
        const qty = this.getItemQuantity(addition.id, item.id);
        if (qty > 0 && item.price) {
          total += item.price * qty;
        }
      });
    });

    Object.keys(this.selectedSupplements)?.forEach(supplementId => {
      const supplement = this.product?.supplements?.find((s: any) => s.id === supplementId);
      if (supplement) {
        total += this.getSupplementPrice(supplement);
      }
    });

    return Number((total * this.productQuantity).toFixed(2));
  }

  canAdd(): boolean {
    return this.getFilteredAdditions().every((addition: any) => {
      if (addition.required) {
        return this.getTotalQuantityForAddition(addition.id) === addition.quantity;
      }
      return true;
    }) ?? true;
  }


  confirm() {
    if (!this.canAdd()) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        'Veuillez compléter les choix obligatoires',
        ''
      );
      return;
    }

    const result = {
      product: this.product,
      quantity: this.productQuantity,
      selectedSize: this.selectedSize,
      selectedPrice: this.selectedPrice,

      selectedIngredients: this.selectedIngredients,
      excludedIngredients: this.excludedIngredients,

      selectedAdditions: this.selectedItemQuantities,

      selectedSupplements: this.selectedSupplements,

      totalPrice: this.calculateTotal(),

      hasCustomizations: this.excludedIngredients.length > 0 ||
        Object.keys(this.selectedSupplements).length > 0 ||
        Object.values(this.selectedItemQuantities).some(items =>
          Object.values(items).some(qty => qty > 0)
        )
    };

    this.dialogRef.close(result);
  }

  getValidationMessage(): string {
    if (!this.product?.additions) return '';

    for (const addition of this.getFilteredAdditions()) {
      if (addition.required) {
        const total = this.getTotalQuantityForAddition(addition.id);
        if (total < addition.quantity) {
          const remaining = addition.quantity - total;
          const categoryName = this.getAdditionTitle(addition);
          return `Choisissez "${categoryName}" (${remaining} choix restant${remaining > 1 ? 's' : ''})`;
        }
      }
    }
    return '';
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
